import { HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

/**
 * Modo ayuda del Asistente Arancelario: el glosario en llano de los términos
 * que la ficha y el simulador dan por sabidos (feedback de Sergio: quien
 * recién entra a comercio exterior no tiene por qué saber qué es el CIF).
 *
 * Las definiciones explican, no legislan: las citas legales viven en las
 * tarjetas y en la fórmula; acá solo se repiten cuando ayudan a entender.
 * Los 4 tributos llevan su color de identidad — el mismo de su tarjeta y de
 * su paso en la fórmula.
 */

interface Termino {
  termino: string;
  definicion: string;
  /** Color de identidad (los 4 tributos). Clase del puntito. */
  color?: string;
}

interface GrupoGlosario {
  titulo: string;
  terminos: Termino[];
}

const GLOSARIO: GrupoGlosario[] = [
  {
    titulo: 'Los montos',
    terminos: [
      {
        termino: 'FOB',
        definicion:
          'Lo que cuesta la mercadería puesta en el punto de embarque del país de origen ' +
          '(del inglés "Free On Board"). Es el precio de la factura del proveedor, antes ' +
          'del flete y el seguro internacionales.',
      },
      {
        termino: 'Flete',
        definicion:
          'El costo del transporte internacional, desde el embarque hasta la aduana de ' +
          'frontera boliviana. Entra en la base de los impuestos: a flete más caro, más tributos.',
      },
      {
        termino: 'Seguro',
        definicion:
          'La prima del seguro de la carga durante el trayecto internacional. También entra ' +
          'en la base de los impuestos.',
      },
      {
        termino: 'CIF',
        definicion:
          'FOB + flete + seguro (del inglés "Cost, Insurance & Freight"). Es EL número de la ' +
          'aduana boliviana: la base sobre la que se calcula el GA, y el punto de partida de ' +
          'toda la liquidación.',
      },
      {
        termino: 'Tipo de cambio (TC)',
        definicion:
          'Cuántos bolivianos vale un dólar para esta liquidación. En aduana se usa el ' +
          'oficial de venta del BCB. Prixia todavía no lo carga solo — se ingresa a mano.',
      },
    ],
  },
  {
    titulo: 'Los tributos',
    terminos: [
      {
        termino: 'GA — Gravamen Arancelario',
        color: 'bg-info',
        definicion:
          'El arancel de importación propiamente dicho: un porcentaje sobre el CIF que el ' +
          'arancel nacional fija partida por partida (va de 0 % a 40 %). Es el único tributo ' +
          'que las preferencias arancelarias pueden rebajar.',
      },
      {
        termino: 'IVA a las importaciones',
        color: 'bg-violet-500',
        definicion:
          'El mismo IVA del mercado interno, cobrado en la aduana al nacionalizar. El 13 % ' +
          'va "por dentro" del precio, así que la tasa que de verdad se aplica es ' +
          '13 ÷ 87 = 14,94 % sobre CIF + GA. Por eso el número no cuadra si calculás el 13 % directo.',
      },
      {
        termino: 'ICE — Impuesto a los Consumos Específicos',
        color: 'bg-alerta',
        definicion:
          'Grava consumos específicos: vehículos, bebidas alcohólicas, tabaco. El arancel ' +
          'marca QUÉ partidas lo pagan, no cuánto: para vehículos la tasa depende de la ' +
          'antigüedad (Apéndice I del arancel) y para alcohol y tabaco la fija la Ley 843.',
      },
      {
        termino: 'IEHD — Impuesto Especial a los Hidrocarburos',
        color: 'bg-ok',
        definicion:
          'Grava hidrocarburos y derivados (combustibles). Se liquida por litro o por unidad ' +
          'de medida — no es un porcentaje del CIF, por eso el simulador no lo incluye.',
      },
      {
        termino: 'Base imponible',
        definicion:
          'El monto sobre el que se calcula un impuesto. Para el GA la base es el CIF; para ' +
          'el IVA es CIF + GA (+ ICE cuando corresponde). El orden importa: por eso el GA se ' +
          'calcula primero.',
      },
      {
        termino: 'Total tributos',
        definicion: 'GA + IVA (+ ICE / IEHD si corresponden): todo lo que se paga en la aduana.',
      },
      {
        termino: 'Costo nacionalizado',
        definicion:
          'CIF + tributos: lo que de verdad costó poner la mercadería dentro del país. Es el ' +
          'número que hay que usar para calcular márgenes y precios de venta — usar solo el ' +
          'FOB subestima el costo real.',
      },
    ],
  },
  {
    titulo: 'La clasificación y los papeles',
    terminos: [
      {
        termino: 'Partida arancelaria',
        definicion:
          'La clasificación de la mercadería en el arancel. El código de 10 dígitos se lee ' +
          'por niveles: 4 dígitos = partida (mundial), 6 = subpartida del Sistema Armonizado, ' +
          '8 = NANDINA (comunidad andina), 10 = apertura propia de Bolivia. De la partida ' +
          'salen el GA, el ICE y todos los requisitos.',
      },
      {
        termino: 'Preferencia arancelaria',
        definicion:
          'La rebaja del GA cuando la mercadería viene de un país con acuerdo comercial ' +
          '(CAN, ACE 22 — Chile, ACE 36 — Mercosur, ACE 66 — México). "100" = GA liberado del ' +
          'todo. Solo rebaja el GA: el IVA y el ICE se pagan igual.',
      },
      {
        termino: 'Certificado de origen',
        definicion:
          'El documento que prueba en qué país se produjo la mercadería. Sin certificado ' +
          'válido no hay preferencia, aunque el acuerdo exista.',
      },
      {
        termino: 'Documento de despacho (permiso previo)',
        definicion:
          'El papel que el arancel exige para ciertas partidas antes de despachar: ' +
          'certificados del SENASAG, autorizaciones previas, declaraciones juradas... Sin ese ' +
          'documento la mercadería no entra, aunque los impuestos estén pagados.',
      },
      {
        termino: 'Despacho en frontera',
        definicion:
          'La marca del arancel que obliga a hacer el trámite aduanero en la aduana de ' +
          'frontera, no en una aduana interior. Cambia la logística: la carga se detiene ' +
          'en frontera para nacionalizarse.',
      },
    ],
  },
];

/**
 * Botón + diálogo del glosario. El trigger se estila desde afuera con
 * `variant`/`size` estándar de Button para encajar donde se lo ponga.
 */
export function GlosarioAduanero({ compacto = false }: { compacto?: boolean }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="text-muted-foreground h-7 px-2">
          <HelpCircle className="h-4 w-4 mr-1.5" />
          {compacto ? 'Ayuda' : '¿Qué significan estos términos?'}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Glosario aduanero</DialogTitle>
          <DialogDescription>
            Los términos de la ficha y del simulador, explicados en llano. Las normas exactas están
            citadas en cada tarjeta y en la fórmula de liquidación.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          {GLOSARIO.map((grupo) => (
            <div key={grupo.titulo} className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b pb-1">
                {grupo.titulo}
              </p>
              <dl className="space-y-3">
                {grupo.terminos.map((t) => (
                  <div key={t.termino}>
                    <dt className="text-sm font-semibold flex items-center gap-2">
                      {t.color && <span className={`h-2 w-2 rounded-full shrink-0 ${t.color}`} />}
                      {t.termino}
                    </dt>
                    <dd className="text-sm text-muted-foreground mt-0.5">{t.definicion}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
